import { useState } from "react";
import { ChevronLeft } from "react-feather";
import { COMPANY_NAME, MIN_PASSWORD_LENGTH, useT } from "@reciperoll/core";
import { supabase } from "../lib/supabase";
import { posthog } from "../lib/posthog";
import { AuthLogo } from "./AuthLogo";
import { AuthSwitchLink } from "./AuthSwitchLink";
import { SocialAuthButtons } from "./SocialAuthButtons";

type Mode = "signin" | "signup" | "forgot";
// Two-step: the chooser (social + "Continue with email") keeps the first screen
// short; the email form only appears once the user opts into it.
type Step = "chooser" | "email";

// Auth entry for web. Social sign-in + email/password, plus forgot-password. On
// success, onAuthStateChange updates the session and AuthGate swaps in the app;
// the reset link opens the set-new-password screen (see AuthGate).
export function Login() {
  const t = useT();
  const [step, setStep] = useState<Step>("chooser");
  const [mode, setMode] = useState<Mode>("signin");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const clearMessages = () => {
    setError(null);
    setNotice(null);
    setSent(false);
  };

  const switchMode = (next: Mode) => {
    setMode(next);
    clearMessages();
  };

  // Enter the email form in a given mode (from the chooser).
  const goEmail = (next: Mode) => {
    setMode(next);
    clearMessages();
    setStep("email");
  };

  const backToChooser = () => {
    setStep("chooser");
    setMode("signin");
    clearMessages();
  };

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      if (mode === "signin") {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      } else if (mode === "signup") {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            // /confirm (not /auth/confirm) so web + mobile share one confirm path,
            // enabling a single universal-link confirmation URL across platforms.
            emailRedirectTo: `${window.location.origin}/confirm`,
            // Capture the display name at sign-up, same as the RN app. display_name
            // is the durable field (providers never overwrite it); full_name seeds
            // the profiles row via the handle_new_user trigger.
            data: { display_name: name.trim(), full_name: name.trim() },
          },
        });
        if (error) throw error;
        // New account created. Only reaches PostHog if the user has consented to
        // analytics (opt-out by default), so this undercounts — see the Supabase
        // auth webhook for a complete, consent-independent signup notification.
        posthog.capture("user_signed_up", { method: "email" });
        // With email confirmation enabled, no session is returned until confirmed.
        if (!data.session) {
          setNotice(t("auth.checkEmailConfirm"));
          setMode("signin");
        }
      } else {
        // forgot: email a reset link back to the /reset-password route. The PKCE
        // recovery redirect carries no `type=recovery`, so PASSWORD_RECOVERY never
        // fires — the dedicated path is what tells AuthGate to show the set-new-
        // password screen (rather than treating the code exchange as a normal login).
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (error) throw error;
        setSent(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t("auth.somethingWentWrong"));
    } finally {
      setBusy(false);
    }
  }

  const title =
    mode === "signin"
      ? t("auth.signInToSync")
      : mode === "signup"
        ? t("auth.createYourAccount")
        : t("auth.resetSubtitle");

  const submitLabel =
    mode === "signin" ? t("auth.signIn") : mode === "signup" ? t("auth.signUp") : t("auth.sendResetLink");

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-paper p-4">
      <div className="w-full max-w-sm rounded-2xl bg-surface p-8 shadow-sm ring-1 ring-line">
        {step === "chooser" ? (
          <>
            <AuthLogo />
            <p className="mt-3 text-sm text-ink-soft">{t("auth.signInToSync")}</p>

            <div className="mt-6">
              <SocialAuthButtons />
            </div>

            <div className="my-5 flex items-center gap-3">
              <span className="h-px flex-1 bg-line" />
              <span className="text-xs font-medium uppercase tracking-wide text-ink-faint">
                {t("auth.or")}
              </span>
              <span className="h-px flex-1 bg-line" />
            </div>

            <button
              type="button"
              onClick={() => goEmail("signin")}
              className="w-full rounded-lg border border-line bg-surface px-4 py-2.5 text-sm font-semibold text-ink transition hover:bg-surface-2 cursor-pointer"
            >
              {t("auth.continueWithEmail")}
            </button>

            {/* New users can jump straight to the email sign-up form (social
                buttons already handle sign-up; this covers the email path). */}
            <AuthSwitchLink
              onClick={() => goEmail("signup")}
              prompt={t("auth.noAccount")}
              action={t("auth.signUp")}
            />
          </>
        ) : (
          <form onSubmit={submit}>
            <button
              type="button"
              onClick={backToChooser}
              className="mb-4 -ml-1 flex items-center gap-1 text-sm font-semibold text-ink-soft hover:text-ink cursor-pointer"
            >
              <ChevronLeft className="w-4 h-4" strokeWidth={2.5} />
              {t("common.back")}
            </button>

            <AuthLogo />
            <p className="mt-3 text-sm text-ink-soft">{title}</p>

            {mode === "forgot" && sent ? (
              <>
                <p className="mt-6 text-sm text-green-700">
                  {t("auth.resetSentGeneric", { email: email || t("auth.thatAddress") })}
                </p>
                <button
                  type="button"
                  onClick={() => switchMode("signin")}
                  className="mt-6 w-full text-center text-sm font-semibold text-terracotta hover:text-terracotta-dark cursor-pointer"
                >
                  {t("auth.backToSignIn")}
                </button>
              </>
            ) : (
              <>
                {mode === "signup" && (
                  <label className="mt-6 block text-sm font-medium text-ink">
                    {t("account.fullName")}
                    <input
                      type="text"
                      required
                      autoComplete="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="mt-1 w-full rounded-lg border border-line px-3 py-2 text-ink outline-none focus:border-terracotta focus:ring-1 focus:ring-terracotta"
                    />
                  </label>
                )}

                <label className={`${mode === "signup" ? "mt-4" : "mt-6"} block text-sm font-medium text-ink`}>
                  {t("account.email")}
                  <input
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-line px-3 py-2 text-ink outline-none focus:border-terracotta focus:ring-1 focus:ring-terracotta"
                  />
                </label>

                {mode !== "forgot" && (
                  <label className="mt-4 block text-sm font-medium text-ink">
                    {t("account.password")}
                    <input
                      type="password"
                      required
                      minLength={MIN_PASSWORD_LENGTH}
                      autoComplete={mode === "signin" ? "current-password" : "new-password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="mt-1 w-full rounded-lg border border-line px-3 py-2 text-ink outline-none focus:border-terracotta focus:ring-1 focus:ring-terracotta"
                    />
                  </label>
                )}

                {mode === "signin" && (
                  <button
                    type="button"
                    onClick={() => switchMode("forgot")}
                    className="mt-2 block text-sm font-semibold text-terracotta hover:text-terracotta-dark cursor-pointer"
                  >
                    {t("auth.forgotPassword")}
                  </button>
                )}

                {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
                {notice && <p className="mt-4 text-sm text-green-700">{notice}</p>}

                <button
                  type="submit"
                  disabled={busy}
                  className="mt-6 w-full rounded-lg bg-terracotta px-4 py-2.5 font-semibold text-white transition hover:bg-terracotta-dark disabled:opacity-60"
                >
                  {busy ? t("auth.pleaseWait") : submitLabel}
                </button>

                {mode === "forgot" ? (
                  <button
                    type="button"
                    onClick={() => switchMode("signin")}
                    className="mt-4 w-full text-center text-sm font-semibold text-terracotta hover:text-terracotta-dark cursor-pointer"
                  >
                    {t("auth.backToSignIn")}
                  </button>
                ) : (
                  <AuthSwitchLink
                    onClick={() => switchMode(mode === "signin" ? "signup" : "signin")}
                    prompt={
                      mode === "signin"
                        ? t("auth.noAccount")
                        : t("auth.alreadyHaveAccount")
                    }
                    action={mode === "signin" ? t("auth.signUp") : t("auth.signIn")}
                  />
                )}
              </>
            )}
          </form>
        )}
      </div>
      <p className="mt-6 text-xs text-ink-faint">{t("account.copyright", { year: new Date().getFullYear(), company: COMPANY_NAME })}</p>
    </div>
  );
}
